import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart } from '../../components/shared/Charts/BarChart/BarChart';
import { UNIT } from '../page-dashboard-V2/helper';
import { translateProviderRole } from '../../util/translateProviderRole';
import type { KPIYear } from './supplier-company-card';
import './page-sales-tool.scss';

/**
 * Shared, props-driven economics view (company grid, board, Bokslut summary with
 * turnover chart + latest-year figures). Extracted from SupplierCompanyCard so the
 * sales tool and the supplier Economics tab share it; labels use the `salesTool`
 * namespace across both. `fields` hides grid values already shown in the supplier
 * Overview tab; `enableSeeAll` adds the full multi-year bokslut table.
 */
export type EconomicsFieldVisibility = {
  orgNumber?: boolean;
  address?: boolean;
  registrationDate?: boolean;
  industry?: boolean;
  creditLimit?: boolean;
};

type Props = {
  companyInfo: any | null;
  ratingInfo?: any | null;
  kpiData: KPIYear[];
  fallbackOrgNumber?: string;
  fields?: EconomicsFieldVisibility;
  enableSeeAll?: boolean;
};

const fmtTkr = (value: any) => `${Number(value || 0).toLocaleString('sv-SE')} tkr`;

type KpiKind = 'tkr' | 'pct' | 'count';

/**
 * Format a KPI value for display. Missing markers (undefined/null/''/SYNA's "*"/"-") render as
 * a plain "-" so an absent ratio doesn't show as "-%". Shared by the summary and full tables so
 * both format identically.
 */
export const fmtKpi = (value: any, kind: KpiKind): string | number => {
  if (value === undefined || value === null || value === '' || value === '-' || value === '*') return '-';
  if (kind === 'tkr') return fmtTkr(value);
  if (kind === 'pct') return `${value}%`;
  return value;
};

const CompanyEconomicsSection: FC<Props> = ({
  companyInfo,
  ratingInfo,
  kpiData,
  fallbackOrgNumber,
  fields,
  enableSeeAll = false,
}) => {
  const [t] = useTranslation('salesTool');
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);

  const show: Required<EconomicsFieldVisibility> = {
    orgNumber: fields?.orgNumber ?? true,
    address: fields?.address ?? true,
    registrationDate: fields?.registrationDate ?? true,
    industry: fields?.industry ?? true,
    creditLimit: fields?.creditLimit ?? true,
  };

  const displayOrgNumber = companyInfo?.orgNumber ?? fallbackOrgNumber;
  const hasGridFields =
    (show.orgNumber && displayOrgNumber) ||
    (show.address && companyInfo) ||
    (show.registrationDate && companyInfo) ||
    (show.industry && companyInfo?.bransch) ||
    (show.creditLimit && ratingInfo);

  // Every figure SYNA's BNyck returns — amounts, then ratios. (Full line-item
  // resultaträkning/balansräkning isn't available structured from SYNA.)
  const fullRows: { label: string; key: keyof KPIYear; kind: KpiKind }[] = [
    { label: t('company.financials.netRevenue', 'Nettoomsättning'), key: 'nettoomsattning', kind: 'tkr' },
    { label: t('company.financials.revenueGrowth', 'Omsättningstillväxt'), key: 'omsattningstillvaxt', kind: 'pct' },
    { label: t('company.financials.operatingProfit', 'Rörelseresultat'), key: 'rorelseresultat', kind: 'tkr' },
    { label: t('company.financials.netProfit', 'Årets resultat'), key: 'aretsresultat', kind: 'tkr' },
    { label: t('company.financials.operatingMargin', 'Rörelsemarginal'), key: 'rorelsemarginal', kind: 'pct' },
    { label: t('company.financials.totalAssets', 'Summa tillgångar'), key: 'balansomslutning', kind: 'tkr' },
    { label: t('company.financials.equity', 'Eget kapital'), key: 'egetKapital', kind: 'tkr' },
    { label: t('company.financials.totalLiabilities', 'Summa skulder'), key: 'summaSkulder', kind: 'tkr' },
    { label: t('company.financials.solidity', 'Soliditet'), key: 'soliditet', kind: 'pct' },
    { label: t('company.financials.cashLiquidity', 'Kassalikviditet'), key: 'kassalikviditet', kind: 'pct' },
    { label: t('company.financials.employees', 'Anställda'), key: 'anstallda', kind: 'count' },
    { label: t('company.financials.revenuePerEmployee', 'Omsättning per anställd'), key: 'omsattningPerAnstalld', kind: 'tkr' },
  ];

  // Hide rows the company has no value for in any year, so a provider only shows what it has
  // (SYNA lacks operating profit / net profit / total liabilities; CreditSafe lacks per-year
  // employees). A row with data in at least one year is kept.
  const rowHasData = (v: any) => v !== undefined && v !== null && v !== '' && v !== '-' && v !== '*';
  const visibleFullRows = fullRows.filter((row) => kpiData.some((y) => rowHasData(y[row.key])));

  // Gracefully handle fields absent from older cached docs or SYNA's "*" placeholder.
  const renderFullCell = (row: { key: keyof KPIYear; kind: KpiKind }, year: KPIYear) =>
    fmtKpi(year[row.key], row.kind);

  // Convert a cell to a chartable number (tkr/count are already numbers; pct arrives as
  // a Swedish string like "50,8"). Missing/"*" values become null so the chart skips them.
  const toChartNumber = (row: { key: keyof KPIYear; kind: string }, year: KPIYear): number | null => {
    const value = year[row.key];
    if (value === undefined || value === null || value === '' || value === '-') return null;
    if (typeof value === 'number') return value;
    const n = parseFloat(String(value).replace(/\s| /g, '').replace(',', '.'));
    return isNaN(n) ? null : n;
  };

  const unitForKind = (kind: string) =>
    kind === 'tkr' ? UNIT.AMOUNT_TKR : kind === 'pct' ? UNIT.PERCENTAGE : undefined;

  return (
    <>
      {hasGridFields && (
        <div className="sales-tool__company-card-grid">
          {show.orgNumber && displayOrgNumber && (
            <div className="sales-tool__company-card-field">
              <div className="sales-tool__company-card-field-label">{t('company.orgNumber', 'Org.nummer')}</div>
              <div className="sales-tool__company-card-field-value">{displayOrgNumber}</div>
            </div>
          )}
          {show.address && companyInfo && (
            <div className="sales-tool__company-card-field">
              <div className="sales-tool__company-card-field-label">{t('company.address', 'Adress')}</div>
              <div className="sales-tool__company-card-field-value">
                {companyInfo.address}, {companyInfo.postcode} {companyInfo.city}
              </div>
            </div>
          )}
          {show.registrationDate && companyInfo && (
            <div className="sales-tool__company-card-field">
              <div className="sales-tool__company-card-field-label">{t('company.registrationDate', 'Registreringsdatum')}</div>
              <div className="sales-tool__company-card-field-value">
                {companyInfo.regdatum?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
              </div>
            </div>
          )}
          {show.industry && companyInfo?.bransch && (
            <div className="sales-tool__company-card-field">
              <div className="sales-tool__company-card-field-label">{t('company.industry', 'Bransch')}</div>
              <div className="sales-tool__company-card-field-value">{companyInfo.bransch}</div>
            </div>
          )}
          {show.creditLimit && ratingInfo && (
            <div className="sales-tool__company-card-field">
              <div className="sales-tool__company-card-field-label">{t('company.creditLimit', 'Kreditlimit')}</div>
              <div className="sales-tool__company-card-field-value">
                {ratingInfo.limit && !Number.isNaN(Number(ratingInfo.limit))
                  ? `${Number(ratingInfo.limit).toLocaleString('sv-SE')} kr`
                  : '-'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Board / Ledning & Styrelse */}
      {companyInfo?.board?.length > 0 && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('company.board', 'Ledning & Styrelse')}</h4>
          <div className="sales-tool__company-card-grid">
            {companyInfo.board.map((member: any, i: number) => (
              <div key={i} className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">{translateProviderRole(member.role, t)}</div>
                <div className="sales-tool__company-card-field-value">{member.name}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Bokslut / Financial Statements */}
      {kpiData.length > 0 && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('company.financials.title', 'Bokslut')}</h4>
          <div className="sales-tool__bokslut">
            <div className="sales-tool__bokslut-chart">
              <BarChart
                title=""
                data={kpiData.map(k => ({
                  name: k.period,
                  [t('chart.revenueLabel', 'Omsättning')]: k.omsattning,
                  [t('chart.resultLabel', 'Resultat')]: k.resultat,
                }))}
                index="name"
                categories={[t('chart.revenueLabel', 'Omsättning'), t('chart.resultLabel', 'Resultat')]}
                colors={['emerald', 'amber']}
                unit={UNIT.AMOUNT_TKR}
              />
            </div>
            <div className="sales-tool__bokslut-table">
              <div className="sales-tool__bokslut-header">
                <span>{t('company.financials.title', 'Bokslut')}</span>
                <span>{kpiData[0]?.period}</span>
              </div>
              {[
                { label: t('company.financials.revenue', 'Omsättning'), value: kpiData[0]?.omsattning, kind: 'tkr' as KpiKind, highlight: true },
                { label: t('company.financials.result', 'Resultat efter finansnetto'), value: kpiData[0]?.resultat, kind: 'tkr' as KpiKind, highlight: true },
                { label: t('company.financials.totalAssets', 'Summa tillgångar'), value: kpiData[0]?.balansomslutning, kind: 'tkr' as KpiKind },
                { label: t('company.financials.equity', 'Eget kapital'), value: kpiData[0]?.egetKapital, kind: 'tkr' as KpiKind, highlight: true },
                { label: t('company.financials.employees', 'Anställda'), value: kpiData[0]?.anstallda, kind: 'count' as KpiKind },
                { label: t('company.financials.solidity', 'Soliditet'), value: kpiData[0]?.soliditet, kind: 'pct' as KpiKind },
                { label: t('company.financials.cashLiquidity', 'Kassalikviditet'), value: kpiData[0]?.kassalikviditet, kind: 'pct' as KpiKind },
              ].map((row, i: number) => (
                <div key={i} className={`sales-tool__bokslut-row ${row.highlight ? 'sales-tool__bokslut-row--highlight' : ''}`}>
                  <span>{row.label}</span>
                  <span>{fmtKpi(row.value, row.kind)}</span>
                </div>
              ))}
              <div className="sales-tool__bokslut-row sales-tool__bokslut-row--footer">
                <span>{t('company.financials.currencyNote', 'Valutakod (Belopp i 1000)')}</span>
                <span>SEK</span>
              </div>
            </div>
          </div>

          {/* Full multi-year bokslut (Allabolag-style) */}
          {enableSeeAll && kpiData.length > 0 && (
            <div className="sales-tool__bokslut-seeall">
                <div className="sales-tool__bokslut-full-scroll" style={{ marginTop: 12 }}>
                  <table className="sales-tool__bokslut-full-table">
                    <thead>
                      <tr>
                        <th>{t('company.financials.fullTableMetric', 'Bokslut')}</th>
                        {kpiData.map((y, i) => <th key={i}>{y.period}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleFullRows.map((row) => {
                        const isExpanded = expandedMetric === row.key;
                        return (
                          <Fragment key={row.key as string}>
                            <tr
                              className={`sales-tool__bokslut-full-row ${isExpanded ? 'sales-tool__bokslut-full-row--expanded' : ''}`}
                              role="button"
                              tabIndex={0}
                              aria-expanded={isExpanded}
                              onClick={() => setExpandedMetric(isExpanded ? null : (row.key as string))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setExpandedMetric(isExpanded ? null : (row.key as string));
                                }
                              }}
                            >
                              <td>
                                <span className="sales-tool__bokslut-full-caret" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                                {row.label}
                              </td>
                              {kpiData.map((y, ci) => <td key={ci}>{renderFullCell(row, y)}</td>)}
                            </tr>
                            {isExpanded && (
                              // table-layout:fixed makes this full-width cell bounded, so the
                              // chart's ResponsiveContainer can't feed back into the table width.
                              <tr className="sales-tool__bokslut-full-chartrow">
                                <td colSpan={kpiData.length + 1}>
                                  {/* Chart stays full width; only the bars get thinner when there
                                      are few years. --bar-scale narrows each bar around its own
                                      center (see .sales-tool__bokslut-rowchart), normalized so a
                                      few-year chart's bars match a ~6-year chart's bar width. */}
                                  <div
                                    className="sales-tool__bokslut-rowchart"
                                    style={{ ['--bar-scale' as any]: Math.min(1, kpiData.length / 15) }}
                                  >
                                    <BarChart
                                      title=""
                                      index="name"
                                      data={kpiData.map(y => ({ name: y.period, [row.label]: toChartNumber(row, y) }))}
                                      categories={[row.label]}
                                      colors={['emerald']}
                                      unit={unitForKind(row.kind)}
                                      // Fixed width so %/count rows align their bars with the tkr rows.
                                      yAxisWidth={112}
                                    />
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="sales-tool__bokslut-full-note">
                    {t('company.financials.currencyNote', 'Valutakod (Belopp i 1000)')} — SEK
                  </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CompanyEconomicsSection;
